import { useQuery } from '@tanstack/react-query'
import { RefreshCw } from 'lucide-react'
import { ADMIN_API_BASE_URL, Button, Skeleton } from '../../core/index.js'
import { BridgeCalloutList } from '../../schedule/index.js'
import { listBridgeCallouts, type PartnerSession } from '../lib/partnerApi.js'
import { ErrorNote, describeError } from './formControls.js'

/**
 * Plays a partner that keeps the roster and wants to know who took a
 * shift: reads `GET /facilities/{facilityId}/bridge/callouts` as the
 * partner (`events:write` or `rankings:read`) and shows each callout with
 * its pool and answers. Reading is all a partner may do here: a facility
 * administrator opens callouts, from the dashboard.
 */
export function BridgeCalloutsView({ session, facilityId }: { session: PartnerSession; facilityId: string }) {
  const query = useQuery({
    queryKey: ['partner-simulator', 'bridge-callouts', session.clientId, facilityId],
    queryFn: () => listBridgeCallouts(ADMIN_API_BASE_URL ?? '', session, facilityId),
    enabled: Boolean(ADMIN_API_BASE_URL),
  })
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-ink-mid">The facility's Clinical Bridge callouts as a partner reads them, newest first: who was told, who answered, who took the shift. A partner reads; only a facility administrator opens one.</p>
        <Button icon={RefreshCw} variant="secondary" size="sm" disabled={query.isFetching} onClick={() => void query.refetch()}>
          Refresh
        </Button>
      </div>
      {query.isPending ? <Skeleton /> : query.isError ? <ErrorNote message={describeError(query.error, 'The callouts could not be read.')} onRetry={() => void query.refetch()} retrying={query.isFetching} /> : <BridgeCalloutList callouts={query.data} empty="No callout has been opened at this facility." />}
    </div>
  )
}
